package ui.campaign.detailv2.widgets

import androidx.compose.foundation.border
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import common.frontend.api.Io
import compose.assets.Svg
import compose.assets.svg.Clock_outline
import kotlinx.datetime.Clock
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toInstant
import ssoft.frontend.generated.resources.Res
import ssoft.frontend.generated.resources.layer_top_detail_editor__miniapp_preview_title
import tools.rememberString

@Composable
fun CampaignInfoCardMiniApp(miniApp: Io.Campaign.MiniApp) {
    if (!miniApp.enabled) return


    val contentColor = Color(miniApp.fg)
    val surfaceColor = Color(miniApp.bg)

    var timeLeft by remember(miniApp.dt) {
        mutableStateOf(miniAppTimeLeft(miniApp.dt))
    }

    LaunchedEffect(miniApp.dt) {
        while (true) {
            timeLeft = miniAppTimeLeft(miniApp.dt)
            kotlinx.coroutines.delay(1000)
        }
    }

    CampaignInfoCardWrapper(
        icon = Svg.Clock_outline,
        label = Res.string.layer_top_detail_editor__miniapp_preview_title.rememberString()
    ) {
        if (!timeLeft.error && !timeLeft.ended) {
            Card(
                shape = RoundedCornerShape(8.dp),
                colors = CardDefaults.cardColors(
                    containerColor = surfaceColor,
                    contentColor = contentColor
                ),
                elevation = CardDefaults.cardElevation(16.dp),
                modifier = Modifier
                    .fillMaxWidth()
                    .border(
                        width = 1.dp,
                        color = MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.15f),
                        shape = RoundedCornerShape(8.dp)
                    )
            ) {
                Row(
                    verticalAlignment = Alignment.Top,
                    horizontalArrangement = Arrangement.Center,
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 32.dp, vertical = 20.dp)
                ) {
                    MiniAppTimeSegment(value = timeLeft.days, label = miniApp.labels.days, color = contentColor)
                    MiniAppColonSeparator(color = contentColor)
                    MiniAppTimeSegment(value = timeLeft.hours, label = miniApp.labels.hours, color = contentColor)
                    MiniAppColonSeparator(color = contentColor)
                    MiniAppTimeSegment(value = timeLeft.minutes, label = miniApp.labels.minutes, color = contentColor)
                    MiniAppColonSeparator(color = contentColor)
                    MiniAppTimeSegment(value = timeLeft.seconds, label = miniApp.labels.seconds, color = contentColor)
                }
            }
        } else {
            Card(
                shape = RoundedCornerShape(8.dp),
                colors = CardDefaults.cardColors(
                    containerColor = surfaceColor,
                    contentColor = contentColor
                ),
                elevation = CardDefaults.cardElevation(16.dp),
                modifier = Modifier.fillMaxWidth()
            ) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 32.dp, vertical = 20.dp),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = miniApp.labels.ended,
                        style = MaterialTheme.typography.headlineMedium,
                        fontWeight = FontWeight.Bold,
                        textAlign = TextAlign.Center,
                        color = contentColor
                    )
                }
            }
        }
    }
}

private data class MiniAppTimeLeftComponents(
    val days: Long,
    val hours: Long,
    val minutes: Long,
    val seconds: Long,
    val ended: Boolean,
    val error: Boolean
)

private fun miniAppTimeLeft(target: LocalDateTime): MiniAppTimeLeftComponents {
    val nowInstant = Clock.System.now()
    val targetInstant = try {
        target.toInstant(TimeZone.currentSystemDefault())
    } catch (e: Exception) {
        return MiniAppTimeLeftComponents(0, 0, 0, 0, ended = true, error = true)
    }
    val diffMs = targetInstant.toEpochMilliseconds() - nowInstant.toEpochMilliseconds()
    if (diffMs <= 0) return MiniAppTimeLeftComponents(0, 0, 0, 0, ended = true, error = false)
    val diffSeconds = diffMs / 1000
    val days = diffSeconds / (24 * 3600)
    val hours = (diffSeconds % (24 * 3600)) / 3600
    val minutes = (diffSeconds % 3600) / 60
    val secs = diffSeconds % 60
    return MiniAppTimeLeftComponents(days, hours, minutes, secs, ended = false, error = false)
}

@Composable
private fun MiniAppTimeSegment(value: Long, label: String, color: Color) {
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = Modifier.width(64.dp)
    ) {
        Text(
            text = value.toString().padStart(2, '0'),
            style = MaterialTheme.typography.displaySmall.copy(
                fontWeight = FontWeight.Bold,
                color = color
            ),
            textAlign = TextAlign.Center
        )
        Spacer(modifier = Modifier.height(4.dp))
        Text(
            text = label,
            style = MaterialTheme.typography.bodyMedium.copy(
                fontWeight = FontWeight.Medium,
                color = color.copy(alpha = 0.5f)
            ),
            textAlign = TextAlign.Center
        )
    }
}

@Composable
private fun MiniAppColonSeparator(color: Color) {
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Top
    ) {
        Text(
            text = ":",
            style = MaterialTheme.typography.displaySmall.copy(
                fontWeight = FontWeight.Light,
                color = color.copy(alpha = 0.5f)
            ),
            textAlign = TextAlign.Center
        )
        Spacer(modifier = Modifier.height(4.dp))
        Text(
            text = "",
            style = MaterialTheme.typography.bodyMedium
        )
    }
}
